/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type PropsWithChildren, useEffect, useMemo, useRef } from 'react';

// React Router
import {
    type DataStrategyResult,
    isRouteErrorResponse,
    Links,
    Meta,
    type MetaDescriptor,
    type MiddlewareFunction,
    Navigate,
    Outlet,
    Scripts,
    ScrollRestoration,
    useRevalidator,
    useRouteLoaderData,
} from 'react-router';
import type { Route } from './+types/root';
import { routes } from '@/route-paths';

// Third-party libraries
import { createInstance, type i18n } from 'i18next';
import { I18nextProvider, useTranslation, initReactI18next } from 'react-i18next';
import { PageDesignerProvider } from '@salesforce/storefront-next-runtime/design/react/core';
import { isDesignModeActive, isPreviewModeActive } from '@salesforce/storefront-next-runtime/design/mode';
import { dataStoreMiddleware, getGcpApiKey } from '@salesforce/storefront-next-runtime/data-store';
import {
    buildUrl,
    SiteProvider,
    siteContext,
    type Site,
    type Locale,
} from '@salesforce/storefront-next-runtime/site-context';

// Middlewares
import authMiddlewareServer, { getAuth as getAuthServer } from '@/middlewares/auth.server';
import { getPublicSessionData } from '@/middlewares/auth.utils';
import createBasketMiddleware, { basketResourceContext, type BasketSnapshot } from '@/middlewares/basket.server';
import shopperContextMiddlewareServer from '@/middlewares/shopper-context.server';
import legacyRoutesMiddlewareClient from '@/middlewares/legacy-routes.client';
import {
    performanceMetricsMiddlewareClient,
    performanceMetricsMiddlewareServer,
} from '@/middlewares/performance-metrics';
import { appConfigMiddlewareServer } from '@/middlewares/app-config.server';
import { appConfigMiddlewareClient } from '@/middlewares/app-config.client';
import { ConfigProvider, getConfig } from '@salesforce/storefront-next-runtime/config';
import type { AppConfig } from '@/types/config';
import { siteContextMiddleware } from '@/middlewares/site-context.server';
import { i18nextMiddleware } from '@/middlewares/i18next.server';
// @sfdc-extension-block-start SFDC_EXT_STORE_LOCATOR
import {
    selectedStoreMiddleware,
    selectedStoreContext,
} from '@/extensions/store-locator/middlewares/selected-store.server';
import type { SelectedStoreInfo } from '@/extensions/store-locator/stores/store-locator-store';
// @sfdc-extension-block-end SFDC_EXT_STORE_LOCATOR
import { correlationMiddleware } from '@/middlewares/correlation.server';
import { requestOriginMiddleware } from '@/middlewares/request-origin';
import { getAppOrigin } from '@/lib/origin';
import { loggingMiddleware } from '@/middlewares/logging.server';
import { pageDesignerResolutionMiddleware } from '@/middlewares/page-designer-page-resolution.server';
import { siteUrlConfigMiddleware } from '@/middlewares/site-url-config.server';
import { modeDetectionMiddlewareServer, modeDetectionMiddlewareClient } from '@/middlewares/mode-detection';
import { maintenanceMiddleware } from '@/middlewares/maintenance.server';
import { securityHeadersMiddleware } from '@/middlewares/security-headers.server';
import { canonicalCaseMiddleware } from '@/middlewares/canonical-case.server';
import { getSecurityNonce } from '@salesforce/storefront-next-runtime/security';
import { useSecurityNonceFromContext } from '@salesforce/storefront-next-runtime/security/react';

// Providers
import AuthProvider from '@/providers/auth';
import BasketProvider from '@/providers/basket';
import { ComposeProviders } from '@/providers/compose-providers';
import { CorrelationProvider } from '@/providers/correlation';
import { correlationContext } from '@/lib/correlation';

// Components
import { AppToaster } from '@/components/toast';
import { ErrorBody } from '@/components/route-error/error-body';
import ShopperAgent from '@/components/shopper-agent';
import Attentive from '@/components/attentive';
import SierraChat from '@/components/sierra-chat';
import ChatLinkInterceptor from '@/components/sierra-chat/chat-link-interceptor';
import OneTrustConsentBridge from '@/components/onetrust/consent-bridge';
import CookieSettingsLinkInterceptor from '@/components/onetrust/cookie-settings-link-interceptor';
import HashAnchorScroll from '@/components/hash-anchor-scroll';
import DatadogRumInit from '@/components/datadog';
import { buildDatadogLoaderSnippet, DATADOG_RUM_AGENT_SRC } from '@/components/datadog/datadog-rum.utils';
import { useReportErrorToDatadog } from '@/components/datadog/use-report-error';

// Hooks
import { useExecutePendingAction } from '@/hooks/use-execute-pending-action';

// Lib/Utils
import type { PublicSessionData } from '@/lib/api/types';
import { getTranslation } from '@salesforce/storefront-next-runtime/i18n';
import { initI18next } from '@salesforce/storefront-next-runtime/i18n/client';
import { PageViewTracker } from '@/analytics/page-view-tracker';
import { AdobeDataLayerTracker } from '@/analytics/adobe-data-layer';
import { DataLayerTracker } from '@/analytics/data-layer-tracker';
import { DatadogViewTracker } from '@/analytics/datadog-view-tracker';
import { initializeRegistry } from '@/lib/page-designer/static-registry';
import { registerEagerComponents } from '@/lib/page-designer/eager-components';
import { buildSeoMetaDescriptors } from '@/utils/seo';

// Fonts
import sen from '/fonts/sen-variable.woff2';

// Styles
import { PageDesignerInit } from '@/page-designer-init';
import appStylesHref from './theme/index.css?url';

// Extensions
import { UITargetProviders } from '@/targets/ui-target-providers';
// @sfdc-extension-block-start SFDC_EXT_STORE_LOCATOR
import StoreLocatorProvider from '@/extensions/store-locator/providers/store-locator';
// @sfdc-extension-block-end SFDC_EXT_STORE_LOCATOR
import { type Maintenance, maintenanceContext } from '@/lib/maintenance';

// Layout Components - logo for error page
import logo from '/images/logo.svg';

// Favicon set (Black Magic). Imported rather than hard-coded as "/favicon.ico": Managed Runtime
// serves `build/client` *only* under `/mobify/bundle/<bundleId>/client/` — nothing is mounted at
// the root — so a literal root path 404s in production while working fine under `pnpm dev` (Vite's
// dev server does serve `public/` at the root). Importing lets Vite rewrite each URL to the runtime
// bundle path, the same mechanism the font and logo imports above rely on. See UCR-3007.
import faviconIco from '/favicon.ico';
import favicon16 from '/favicon-16x16.png';
import favicon32 from '/favicon-32x32.png';
import favicon48 from '/favicon-48x48.png';
import iconSvg from '/icon.svg';
import appleTouchIcon from '/apple-touch-icon.png';
// NOTE: no manifest import here. Managed Runtime's edge refuses to serve `.webmanifest` from the
// bundle path (403 AccessDenied) even though the file ships inside the bundle, so the manifest is
// generated at the root path by `rootAssets` instead. See src/middlewares/root-assets.server.ts.

export const links: Route.LinksFunction = () => {
    return [
        // Preload critical fonts
        { rel: 'preload', href: sen, as: 'font', type: 'font/woff2', crossOrigin: 'anonymous' },
        // Adobe Typekit — Acumin Pro family (kit hcz2fkx). Self-hosting isn't
        // permitted under the Adobe Fonts license, so the kit loads from Typekit.
        { rel: 'preconnect', href: 'https://use.typekit.net', crossOrigin: 'anonymous' },
        { rel: 'stylesheet', href: 'https://use.typekit.net/hcz2fkx.css' },
        { rel: 'preload', href: appStylesHref, as: 'style' },
        { rel: 'stylesheet', href: appStylesHref },
    ];
};

export const meta: Route.MetaFunction = ({ loaderData }) => {
    return loaderData?.seoMeta ?? [];
};

export const middleware: MiddlewareFunction<Response>[] = [
    correlationMiddleware,
    requestOriginMiddleware,
    loggingMiddleware,
    modeDetectionMiddlewareServer,
    appConfigMiddlewareServer,
    securityHeadersMiddleware,
    // Needs only appConfig (for `url.prefix`), but sits inside securityHeaders so its thrown 301
    // picks up the security headers that middleware applies to thrown Responses (UCR-3053).
    canonicalCaseMiddleware,
    siteContextMiddleware, // Must run after appConfig, before i18next and currency
    ...dataStoreMiddleware,
    siteUrlConfigMiddleware, // Must run after siteContextMiddleware (entry key uses site id)
    i18nextMiddleware,
    pageDesignerResolutionMiddleware,
    selectedStoreMiddleware /** @sfdc-extension-line SFDC_EXT_STORE_LOCATOR */,
    performanceMetricsMiddlewareServer,
    maintenanceMiddleware,
    authMiddlewareServer,
    createBasketMiddleware(),
    shopperContextMiddlewareServer,
];

export const clientMiddleware: MiddlewareFunction<Record<string, DataStrategyResult>>[] = [
    // Client middleware functions have varying return types, but React Router expects Record<string, DataStrategyResult>
    // We cast through unknown to avoid type errors while maintaining runtime correctness
    appConfigMiddlewareClient as unknown as MiddlewareFunction<Record<string, DataStrategyResult>>, // Must run first to set config in context
    modeDetectionMiddlewareClient,
    legacyRoutesMiddlewareClient as unknown as MiddlewareFunction<Record<string, DataStrategyResult>>, // Checks hybrid.enabled, needs config from context
    performanceMetricsMiddlewareClient as unknown as MiddlewareFunction<Record<string, DataStrategyResult>>,
];

// On the client side, initialize i18next.
// (On the server side, it's initialized elsewhere in middlewares/i18next.ts file)
// Read the language from the server-rendered HTML to avoid language detection issues
const i18nextOnClient =
    typeof window !== 'undefined'
        ? initI18next({
              language: document.documentElement.lang || undefined,
              // The import() must live here so Vite can resolve the path at build time
              loadLocale: (language) => import(`@/locales/${language}/index.ts`),
          })
        : undefined;

export const loader = ({
    context,
    request,
}: Route.LoaderArgs): {
    // Public auth data - only non-sensitive fields, safe to serialize
    clientAuth: PublicSessionData;
    appConfig: AppConfig;
    basketSnapshot: BasketSnapshot | null;
    maintenance: Maintenance;
    locale: Locale;
    site: Site;
    currency: string;
    selectedStoreInfo: SelectedStoreInfo | null /** @sfdc-extension-line SFDC_EXT_STORE_LOCATOR */;
    correlationId: string;
    pageDesignerMode: 'EDIT' | 'PREVIEW' | undefined;
    // Pre-computed in the loader (server-only) so seo.ts stays out of the client bundle
    seoMeta: MetaDescriptor[];
    // OOTB GCP Address Autocomplete API key sourced from the MRT data store
    gcpApiKeyFromDAL: string;
    // Return as function to prevent i18next instance serialization
    getI18next: () => i18n;
    // Serialized error namespace for the active locale — used by ErrorBoundary on SSR/hydration
    errorTranslations: Record<string, unknown>;
    // CSP nonce for inline scripts; null when security headers middleware is disabled
    nonce: string | null;
} => {
    const session = getAuthServer(context);

    const appConfig = getConfig(context);

    // On the server side, our middleware stores the translations in this i18next object
    // so we'll need to be careful not to accidentally serialize this object (to avoid bloating the html).
    const { i18next } = getTranslation(context);

    // @sfdc-extension-block-start SFDC_EXT_STORE_LOCATOR
    const selectedStoreInfo = context.get(selectedStoreContext) ?? null;
    // @sfdc-extension-block-end SFDC_EXT_STORE_LOCATOR

    // Get resolved site, locale, and currency from site context middleware
    const siteCtx = context.get(siteContext);
    if (!siteCtx) {
        throw new Error('Site context not found. Ensure siteContextMiddleware runs before loaders.');
    }
    const { locale, site, currency } = siteCtx;

    // Load the application basket provider with the basket snapshot. We are actively not loading the basket, as
    // we want to lazy load the basket when the basket is needed. This prevents low-engagement users from causing
    // unnecessary resource usage in the form of basket creations.
    const basketSnapshot = context.get(basketResourceContext)?.snapshot ?? null;

    // Get correlation ID from middleware for request tracing
    const correlationId = context.get(correlationContext);

    // Get maintenance data from middleware
    const maintenance = context.get(maintenanceContext);

    // Extract only non-sensitive fields for client - tokens stay server-side only
    const clientAuth = getPublicSessionData(session);

    const requestUrl = new URL(request.url);

    const seoMeta = buildSeoMetaDescriptors({
        site,
        appConfig,
        // Use the resolved public origin (custom domain on MRT) rather than
        // requestUrl.origin (lambda-internal hostname on hybrid deployments)
        // so canonical/hreflang URLs match what the customer is actually browsing.
        origin: getAppOrigin(context),
        locale,
        location: { pathname: requestUrl.pathname, search: requestUrl.search },
    });

    // CSP nonce produced by securityHeadersMiddleware; null when middleware is disabled.
    const nonce = getSecurityNonce(context);

    return {
        appConfig,
        basketSnapshot,
        locale,
        site,
        currency,
        selectedStoreInfo /** @sfdc-extension-line SFDC_EXT_STORE_LOCATOR */,
        correlationId,
        maintenance,
        clientAuth,
        seoMeta,
        gcpApiKeyFromDAL: getGcpApiKey(context),
        getI18next: () => i18next,
        errorTranslations: (i18next.getResourceBundle(i18next.language, 'routeError') as Record<string, unknown>) ?? {},
        pageDesignerMode: isDesignModeActive(request) ? 'EDIT' : isPreviewModeActive(request) ? 'PREVIEW' : undefined,
        nonce,
    };
};

// This creates a union type where properties unique to either loader are optional
// Properties present in both loaders remain required
type ServerLoaderData = ReturnType<typeof loader>;
type LoaderData = ServerLoaderData;

export function Layout({ children }: PropsWithChildren) {
    const data = useRouteLoaderData<LoaderData>('root');
    const appConfig = data?.appConfig;
    const appConfigScript = appConfig
        ? `window.__APP_CONFIG__ = ${JSON.stringify(appConfig)
              .replace(/</g, '\\u003c')
              .replace(/>/g, '\\u003e')
              .replace(/&/g, '\\u0026')
              .replace(/\u2028/g, '\\u2028')
              .replace(/\u2029/g, '\\u2029')};`
        : '';
    // Adobe Experience Platform tag manager (Launch / Tags) embed URL. Environment
    // driven via config (see config.server.ts \u2192 app.adobeLaunch.src). Empty/missing
    // disables the embed.
    //
    // The embed itself is NOT rendered here any more \u2014 Adobe Analytics is a Performance-category
    // tag, so <OneTrustConsentBridge> injects it once the shopper grants C0002 (UCR-2991). Only the
    // data layer is initialized below, which sets no cookies and issues no requests.
    const adobeLaunchSrc = appConfig?.adobeLaunch?.src;
    // OneTrust cookie consent (UCR-2991). Suppressed inside the Page Designer editor: the banner
    // would otherwise render over the canvas in the merchandiser's iframe. SFRA does the same via
    // its `__SYSTEM__Page-Show` check in htmlHead.isml.
    const oneTrust = appConfig?.oneTrust;
    const oneTrustEnabled =
        Boolean(oneTrust?.enabled && oneTrust.src && oneTrust.domainScript) && !data?.pageDesignerMode;
    // Datadog RUM (UCR-3022). Suppressed inside the Page Designer editor for the same reason as the
    // OneTrust banner: the merchandiser's iframe is not shopper traffic, and its errors and timings
    // would skew every metric the monitors alert on.
    const datadogRum = appConfig?.datadogRum;
    const datadogRumEnabled =
        Boolean(datadogRum?.enabled && datadogRum.applicationId && datadogRum.clientToken) && !data?.pageDesignerMode;
    // React 19 omits the attribute when the value is undefined, so coerce null/missing to undefined.
    // Falls back to the NonceContext so the error path (root loader threw → no
    // loader data) still gets a valid nonce on its inline scripts. The middleware
    // sets `securityContext` *before* `next()`, and `entry.server.tsx` wraps
    // the SSR tree with <NonceContext.Provider>, so the context is populated
    // even when the loader fails.
    const nonceFromContext = useSecurityNonceFromContext();
    const nonce = data?.nonce ?? nonceFromContext ?? undefined;

    const i18next = typeof window === 'undefined' ? data?.getI18next?.() : i18nextOnClient;
    const lang = i18next?.language ?? 'en';
    const dir = i18next?.dir(lang) ?? 'ltr';

    return (
        <html lang={lang} dir={dir}>
            <head>
                <meta charSet="utf-8" />
                {/* Favicon set (Black Magic) — hrefs come from the imports at the top of this file so
                    they carry Managed Runtime's bundle prefix. See UCR-2785, UCR-3007. */}
                <link rel="icon" type="image/x-icon" href={faviconIco} sizes="16x16 32x32 48x48" />
                <link rel="icon" type="image/png" sizes="16x16" href={favicon16} />
                <link rel="icon" type="image/png" sizes="32x32" href={favicon32} />
                <link rel="icon" type="image/png" sizes="48x48" href={favicon48} />
                <link rel="icon" type="image/svg+xml" href={iconSvg} />
                <link rel="apple-touch-icon" sizes="180x180" href={appleTouchIcon} />
                <link rel="manifest" href="/site.webmanifest" />
                <meta name="theme-color" content="#000000" />
                {appConfig?.links?.preconnect?.map((origin: string) => (
                    <link key={origin} rel="preconnect" href={origin} />
                ))}
                {appConfig?.links?.prefetchDns?.map((origin: string) => (
                    <link key={origin} rel="dns-prefetch" href={origin} />
                ))}
                {appConfig?.links?.prefetch?.map((href: string) => (
                    <link key={href} rel="prefetch" href={href} />
                ))}
                <script
                    nonce={nonce}
                    // Browsers strip the `nonce` content attribute from the DOM after
                    // applying CSP (so scripts can't read each other's nonce), leaving
                    // `nonce=""` in the client DOM while the server rendered the real
                    // value. On this hand-written inline script that surfaces as a
                    // hydration attribute mismatch. React's own <Scripts>/<ScrollRestoration>
                    // handle this internally; here we suppress the warning for the same
                    // browser behavior. The CSP nonce itself still applies correctly.
                    //
                    // Note: this also suppresses any server/client divergence of the
                    // __APP_CONFIG__ body, not just the nonce attribute. Safe today
                    // because appConfig is server-rendered and never mutated on the
                    // client. If client-side config mutation is ever introduced, revisit
                    // this — a real config mismatch would be silently hidden here.
                    suppressHydrationWarning
                    dangerouslySetInnerHTML={{
                        __html: `
                        ${appConfigScript}
                    `,
                    }}
                />
                {/* OneTrust cookie consent (UCR-2991). First script in <head>, because everything
                    downstream waits on the choice it collects and the banner should paint early.

                    The shim defines window.OptanonWrapper BEFORE otSDKStub.js runs — the SDK calls
                    it as soon as it knows the shopper's consent, which is long before React
                    hydrates. It cannot call app code directly, so it forwards to __otHandler and
                    parks the callback in __otQueue when nothing is listening yet;
                    <OneTrustConsentBridge> installs the handler and drains the queue. Same
                    queue-then-replay contract as window.adobeDataLayer below.

                    Both scripts carry the nonce and suppress the hydration warning for the same
                    nonce-stripping reason as the __APP_CONFIG__ script above. `integrity` is opt-in
                    via config and empty by default — see config.server.ts → app.oneTrust.

                    OneTrust's snippet also carries charset="UTF-8"; it is omitted because React only
                    permits charSet on <meta>. It is a no-op anyway — the response's own Content-Type
                    wins over the attribute. */}
                {oneTrustEnabled && oneTrust && (
                    <>
                        <script
                            nonce={nonce}
                            suppressHydrationWarning
                            dangerouslySetInnerHTML={{
                                __html: 'window.__otQueue=window.__otQueue||[];window.OptanonWrapper=function(){(window.__otHandler||function(){window.__otQueue.push(1)})()};',
                            }}
                        />
                        <script
                            nonce={nonce}
                            src={oneTrust.src}
                            data-document-language="true"
                            data-domain-script={oneTrust.domainScript}
                            {...(oneTrust.integrity
                                ? { integrity: oneTrust.integrity, crossOrigin: 'anonymous' as const }
                                : {})}
                            async
                            suppressHydrationWarning
                        />
                    </>
                )}
                {/* Adobe Experience Platform tag manager (Launch / Tags, formerly DTM) data layer.
                    Initialized here, in <head>, so page-view pushes from <AdobeDataLayerTracker/>
                    are queued from the very first render — the embed itself is consent-gated
                    (UCR-2991) and injected later by <OneTrustConsentBridge> once C0002 is granted,
                    at which point Launch replays whatever accumulated. Initializing the arrays
                    costs nothing and sets no cookies, so it is not gated. */}
                {adobeLaunchSrc && (
                    <script
                        nonce={nonce}
                        suppressHydrationWarning
                        dangerouslySetInnerHTML={{
                            // ACDL queue (page-view tracker) + the Adobe Analytics data layer
                            // (UCR-2938 pageView/DCRs).
                            __html: 'window.adobeDataLayer=window.adobeDataLayer||[];window.dataLayer=window.dataLayer||[];',
                        }}
                    />
                )}
                {/* Datadog RUM agent loader (UCR-3022). In <head> so the agent is downloading before
                    React hydrates and can attribute the page load it is there to measure — deferring
                    it to a component effect would skew the very numbers RUM reports.

                    This stub only installs the DD_RUM queue and appends the agent <script>; the
                    configuration is supplied later by <DatadogRum> via DD_RUM.onReady(), so the two
                    can arrive in either order. Same queue-then-replay contract as the OneTrust shim
                    above.

                    Carries the nonce and suppresses the hydration warning for the same nonce-stripping
                    reason as the __APP_CONFIG__ script. The agent script this appends carries no
                    nonce — www.datadoghq-browser-agent.com is allowlisted in script-src instead. */}
                {datadogRumEnabled && (
                    <script
                        nonce={nonce}
                        suppressHydrationWarning
                        dangerouslySetInnerHTML={{
                            __html: buildDatadogLoaderSnippet(DATADOG_RUM_AGENT_SRC),
                        }}
                    />
                )}
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                <Meta />
                <Links />
            </head>
            <body className="antialiased flex flex-col min-h-screen">
                {children}
                <AppToaster />
                <ScrollRestoration nonce={nonce} />
                <Scripts nonce={nonce} />
                {/* Dev-only overlay: mounts outside the React tree to avoid interfering with app state/context. Zero production overhead — tree-shaken by Vite when PROD=true. */}
                <UITargetDevModeInit />
            </body>
        </html>
    );
}

/**
 * Standalone error page — the fallback for errors thrown *outside* the `_app` layout (a middleware
 * failure, or a crash before the root loader ran). Storefront route errors do NOT come here: they
 * render in the full chrome via `_app`'s `ErrorBoundary` (UCR-2787), and since UCR-3048 that includes
 * every unmatched URL, because the `_app.$` catch-all is now the single `/*` route.
 *
 * It supplies its own minimal header/footer and shares {@link ErrorBody} with the in-chrome page, so
 * the two can't drift. Every string arrives as a prop: when the root loader never ran there is no
 * I18nextProvider, so the caller passes hardcoded English instead of translations.
 */
function ErrorPageContent({
    status,
    details,
    stack,
    homepageUrl,
    title,
    message,
    secondaryMessage,
    statusDetails,
    goToHomepageLabel,
    allRightsReservedLabel,
}: {
    status: number | undefined;
    details: string | undefined;
    stack: string | undefined;
    homepageUrl: string;
    title: string;
    message?: string;
    secondaryMessage?: string;
    statusDetails?: string;
    goToHomepageLabel: string;
    allRightsReservedLabel: string;
}) {
    return (
        <>
            {/* Simple Header */}
            <header className="bg-header-background text-header-foreground sticky top-0 z-50">
                <div className="section-container">
                    <div className="flex items-center gap-x-4 lg:gap-x-6 h-16">
                        <a href={homepageUrl} className="flex-shrink-0 flex items-center">
                            <img
                                src={logo}
                                alt="Logo"
                                className="h-3 lg:h-4 w-auto [filter:var(--header-logo-filter)]"
                            />
                        </a>
                    </div>
                </div>
            </header>

            {/* Error pages are never indexable, and the tab needs a real title rather than the raw
                URL (UCR-3048 — the in-chrome page gets both from `SeoMeta`). Rendered as plain tags
                and hoisted by React 19: `SeoMeta` itself is not usable here because it reads the
                `common` namespace for the site-name suffix, which this page's cut-down i18next
                instance does not load — and on the pre-loader path there is no provider at all. */}
            <title>{title}</title>
            <meta name="robots" content="noindex" />

            {/* Main Content */}
            <main className="grow pt-8">
                <div className="section-container">
                    <ErrorBody
                        status={status}
                        title={title}
                        message={message}
                        secondaryMessage={secondaryMessage}
                        statusDetails={statusDetails}
                        // Route errors carry their copy in the messages above; a bare Error only has
                        // this technical detail, so it is shown just in that case.
                        detailsText={!status && details ? details : undefined}
                        ctaHref={homepageUrl}
                        ctaLabel={goToHomepageLabel}>
                        {/* Stack trace (only in dev mode with stack) */}
                        {stack && (
                            <div className="mt-16 border border-border rounded-none bg-muted/30 text-left">
                                <div className="flex items-center px-4 py-3 border-b border-border">
                                    <h2 className="text-sm font-semibold text-foreground">Stack Trace</h2>
                                </div>
                                <pre className="p-4 overflow-auto max-h-80 text-xs leading-relaxed text-foreground/90 font-mono">
                                    <code>{stack}</code>
                                </pre>
                                <div className="px-4 py-3 border-t border-border">
                                    <p className="text-xs text-muted-foreground">
                                        To disable stack traces in production, turn off{' '}
                                        <code className="text-xs">unstable_devTools</code> in your router config.
                                    </p>
                                </div>
                            </div>
                        )}
                    </ErrorBody>
                </div>
            </main>

            {/* Simple Footer */}
            <footer className="mt-auto bg-background border-t border-border">
                {/* `section-container` already caps the width (1440px) and centers. The former
                    `mx-auto max-w-7xl` was doing neither: named `max-w-*` sizes are remapped onto this
                    theme's spacing scale, so `max-w-7xl` resolved to a few tens of px. */}
                <div className="section-container py-8">
                    <p className="text-center text-sm text-muted-foreground">
                        © {new Date().getFullYear()} {allRightsReservedLabel}
                    </p>
                </div>
            </footer>
        </>
    );
}

/**
 * Renders the error page using translations from the root loader payload.
 * Requires an I18nextProvider wrapping it.
 */
function TranslatedErrorPageContent({
    status,
    details,
    stack,
    homepageUrl,
}: {
    status: number | undefined;
    details: string | undefined;
    stack: string | undefined;
    homepageUrl: string;
}) {
    const { t } = useTranslation('routeError');

    let title: string;
    let message: string | undefined;
    let secondaryMessage: string | undefined;
    let statusDetails: string | undefined;

    if (status === 404) {
        title = t('404.title');
        message = t('404.message');
        secondaryMessage = t('404.secondaryMessage');
        statusDetails = t('404.details');
    } else if (status === 403) {
        title = t('403.title');
        message = t('403.message');
        secondaryMessage = t('403.secondaryMessage');
    } else if (status === 500) {
        title = t('500.title');
        message = t('500.message');
        secondaryMessage = t('500.secondaryMessage');
    } else {
        title = t('defaultTitle');
    }

    return (
        <ErrorPageContent
            status={status}
            details={details}
            stack={stack}
            homepageUrl={homepageUrl}
            title={title}
            message={message}
            secondaryMessage={secondaryMessage}
            statusDetails={statusDetails}
            goToHomepageLabel={t('goToHomepage')}
            allRightsReservedLabel={t('allRightsReserved')}
        />
    );
}

export function ErrorBoundary({ error }: { error: unknown }) {
    // All hooks must be called unconditionally before any early returns.
    const rootData = useRouteLoaderData<typeof loader>('root');
    // Reports client-navigation failures that escaped `_app` — the fallback below swallows them, so
    // RUM would otherwise never see them (UCR-3022).
    //
    // It cannot report an SSR boot failure, and deliberately does not pretend to: if the root loader
    // throws, `data` is undefined, so `datadogRumEnabled` above is false, the <head> stub is never
    // emitted, and <DatadogRumInit> never mounts — there is no agent to report to. Capturing those
    // would mean loading RUM before we know whether it is configured. Server-side, they are already
    // in the logs Log Center forwards.
    useReportErrorToDatadog(error, 'root');

    const language =
        (typeof window !== 'undefined' && i18nextOnClient ? i18nextOnClient.language : undefined) ||
        (typeof document !== 'undefined' ? document.documentElement.lang : undefined) ||
        rootData?.locale?.id ||
        'en';

    const errorTranslations = rootData?.errorTranslations;
    const fallbackLng = rootData?.appConfig.i18n.fallbackLng ?? 'en';

    // Only created when errorTranslations is available (root loader ran).
    // When the loader did not run (e.g. middleware crash), errorTranslations is undefined and we
    // skip i18next entirely — rendering hardcoded English strings directly avoids any static import
    // of locale JSON that would cause Vite to bundle/preload locale chunks on every page.
    const i18nextInstance = useMemo(() => {
        if (!errorTranslations || Object.keys(errorTranslations).length === 0) return null;
        const resources: Record<string, { routeError: Record<string, unknown> }> = {
            [fallbackLng]: { routeError: errorTranslations },
            [language]: { routeError: errorTranslations },
        };
        const instance = createInstance();
        void instance.use(initReactI18next).init({
            lng: language,
            fallbackLng,
            resources,
            interpolation: { escapeValue: false },
            initAsync: false,
        });
        return instance;
    }, [language, errorTranslations, fallbackLng]);

    const homepageUrl = rootData?.site
        ? buildUrl({
              to: '/',
              urlConfig: rootData.appConfig.url,
              params: {
                  siteId: rootData.site.alias ?? rootData.site.id,
                  localeId: rootData.appConfig.localeAliasMap?.[rootData.locale.id] ?? rootData.locale.id,
              },
          })
        : '/';

    // Redirect maintenance errors before rendering.
    if (error && error.toString().includes('MAINTENANCE_ERROR')) {
        return <Navigate to={routes.maintenance} replace />;
    }

    let status: number | undefined;
    let details: string | undefined;
    let stack: string | undefined;

    if (isRouteErrorResponse(error)) {
        status = error.status;
        details = error.statusText;
    } else if (error instanceof Error) {
        details = error.message;
        stack = error.stack;
    } else if (typeof error === 'string') {
        details = error;
    }

    // Root loader ran — render with active locale translations.
    if (i18nextInstance) {
        return (
            <I18nextProvider i18n={i18nextInstance}>
                <TranslatedErrorPageContent status={status} details={details} stack={stack} homepageUrl={homepageUrl} />
            </I18nextProvider>
        );
    }

    // Root loader did not run (e.g. middleware crash before loader executed).
    // No locale context available — render hardcoded English strings with no i18next dependency.
    const englishContent = {
        404: { title: 'Page not found', message: 'The requested page could not be found.' },
        403: { title: 'Access restricted', message: "You don't have permission to view this page." },
        500: { title: 'Something went wrong', message: 'An unexpected error occurred. Please try again later.' },
    };
    const fallbackContent =
        status && status in englishContent
            ? englishContent[status as keyof typeof englishContent]
            : { title: 'Something went wrong', message: undefined };

    return (
        <ErrorPageContent
            status={status}
            details={details}
            stack={stack}
            homepageUrl={homepageUrl}
            title={fallbackContent.title}
            message={fallbackContent.message}
            goToHomepageLabel="Go to Homepage"
            allRightsReservedLabel="All rights reserved."
        />
    );
}

export default function App({
    loaderData: {
        clientAuth,
        basketSnapshot,
        getI18next,
        currency,
        correlationId,
        pageDesignerMode,
        site,
        locale,
        // @sfdc-extension-block-start SFDC_EXT_STORE_LOCATOR
        selectedStoreInfo,
        // @sfdc-extension-block-end SFDC_EXT_STORE_LOCATOR
    },
}: {
    loaderData: LoaderData;
}) {
    // Currency is always provided by loader (which reads from middleware)
    if (!currency) {
        throw new Error('Currency is required but not provided by loader');
    }

    // Get app configuration from server loader data (initial load) or window.__APP_CONFIG__ (client nav)
    // This ensures config is read from MRT environment variables (via middleware), not baked at build time
    const serverData = useRouteLoaderData('root');
    const appConfig = serverData?.appConfig || (typeof window !== 'undefined' ? window.__APP_CONFIG__ : undefined);
    if (!appConfig) {
        throw new Error('App configuration not available - check server loader and window.__APP_CONFIG__');
    }

    // In server-only auth architecture:
    // - clientAuth contains only non-sensitive fields (userType, customerId, usid, etc.)
    // - These values are serialized directly from the server loader
    // - No client middleware or bootstrap needed - server is the single source of truth
    // - Tokens (accessToken, refreshToken) stay server-side only

    // Initialize Page Designer components
    initializeRegistry();
    // Eagerly register components that must not lazy-load, so they don't flash their fallback during
    // hydration (UCR-2790). Safe in either order vs. initializeRegistry() — see eager-components.ts.
    registerEagerComponents();

    const i18next = (typeof window === 'undefined' ? getI18next?.() : i18nextOnClient) as i18n;

    const sites = appConfig.commerce.sites as AppConfig['commerce']['sites'];
    const defaultSite = sites.find((s) => s.id === appConfig.defaultSiteId) ?? sites[0];
    const shopperAgentLocale = i18next?.language ?? defaultSite?.defaultLocale ?? appConfig.i18n.fallbackLng;

    // Memoize the providers array to prevent unnecessary remounting of providers on render
    const providers = useMemo(
        () =>
            [
                [I18nextProvider, { i18n: i18next }],
                [ConfigProvider, { config: appConfig }],
                // Site provider will contain info about site/locale/currency on single request.
                // include i18next.language since these infos tend to go together.
                // site will drive the language/locale and currency
                [SiteProvider, { site, locale, language: i18next.language, currency }],
                [AuthProvider, { value: clientAuth }],
                [BasketProvider, { snapshot: basketSnapshot }],
                [CorrelationProvider, { value: correlationId }],
                // @sfdc-extension-block-start SFDC_EXT_STORE_LOCATOR
                [StoreLocatorProvider, { selectedStoreInfo }],
                // @sfdc-extension-block-end SFDC_EXT_STORE_LOCATOR
            ] as const,
        [
            correlationId,
            i18next,
            appConfig,
            currency,
            clientAuth,
            basketSnapshot,
            site,
            locale,
            // @sfdc-extension-block-start SFDC_EXT_STORE_LOCATOR
            selectedStoreInfo,
            // @sfdc-extension-block-end SFDC_EXT_STORE_LOCATOR
        ]
    );

    const hybridEnabled = Boolean(appConfig?.hybrid?.enabled);

    return (
        <ComposeProviders providers={providers}>
            {/* Datadog RUM configuration (UCR-3022). The agent is already downloading — the <head>
                stub started it — and this hands it its settings through the DD_RUM queue.

                MUST render above <DatadogViewTracker>. Both reach the agent through DD_RUM.onReady,
                and React commits effects in tree order, so a tracker mounted first would queue
                setViewName ahead of init() — and calls the SDK does not buffer before init are
                discarded, leaving the initial page view (the only one carrying LCP, CLS and INP)
                named by raw URL.

                No pageDesignerMode guard needed: the <head> stub carries that, and without the stub
                this is a no-op. */}
            {appConfig.datadogRum?.enabled && <DatadogRumInit config={appConfig.datadogRum} />}
            <UITargetProviders>
                <AuthActionExecutor />
                {hybridEnabled && <BackNavigationRevalidator />}
                <PageDesignerProvider
                    clientId="storefront-next"
                    targetOrigin="*"
                    usid={clientAuth?.usid}
                    mode={pageDesignerMode}>
                    <PageDesignerInit />
                    {/* Keeps an anchor link (`/about#FAQ`) on its target until the page settles
                        there (UCR-3082) — the browser's one-shot fragment scroll is undone by
                        streamed SSR, by the Page Designer lazy-chunk re-suspend at hydration, and by
                        third-party overlays that pin the scrollport. Renders no UI. Mounted INSIDE
                        <PageDesignerProvider> so its design-mode guard reads the real mode and never
                        fights the editor. */}
                    <HashAnchorScroll />
                    <Outlet />
                </PageDesignerProvider>
                {/* No framework consent banner: cookie consent on Black Magic is owned by OneTrust
                    (UCR-2978/UCR-2991), which draws its own banner from <head>. The framework's
                    <TrackingConsentBanner> is deliberately not mounted — see
                    docs/README-CONSENT-TRACKING.md. <OneTrustConsentBridge> below is what turns the
                    shopper's OneTrust choices into action. */}
                {typeof window !== 'undefined' && <PageViewTracker />}
                {typeof window !== 'undefined' && <AdobeDataLayerTracker />}
                {typeof window !== 'undefined' && <DataLayerTracker />}
                {/* Names each RUM view after the route's own analytics pageName (UCR-3022) — see the
                    component for why Datadog's React Router integration doesn't apply here. */}
                {typeof window !== 'undefined' && <DatadogViewTracker />}
            </UITargetProviders>
            {(appConfig.commerceAgent?.enabled === 'true' || appConfig.commerceAgent?.enabled === true) && (
                <ShopperAgent
                    commerceAgentConfiguration={appConfig.commerceAgent}
                    locale={shopperAgentLocale}
                    currency={currency}
                    userId={clientAuth?.customerId}
                    usid={clientAuth?.usid}
                />
            )}
            {/* Attentive tag — SMS acquisition popup (UCR-2680). Renders no UI of its own; the
                sign-up unit is served by Attentive and managed by CRM in their platform. */}
            {appConfig.attentive?.enabled && appConfig.attentive?.src && (
                <Attentive src={appConfig.attentive.src} fallbackSrc={appConfig.attentive.fallbackSrc} />
            )}
            {appConfig.chat?.enabled && appConfig.chat?.agentUrl && (
                <SierraChat locale={shopperAgentLocale} userId={clientAuth?.customerId} />
            )}
            {/* Opens the chatbot when a content-authored chat CTA is clicked (UCR-2926). Mounted
                UNGATED (UCR-3021): with the chat disabled, an authored "Open chat" CTA still renders
                its `#open-chat` sentinel href, and without a listener the browser would follow it and
                push a junk hash onto the history stack. The interceptor swallows the click instead
                (openSierraChat is fail-soft when the embed is absent), so the URL stays clean. It
                renders no UI and installs one document listener, so mounting it always is cheap. */}
            <ChatLinkInterceptor />
            {/* OneTrust consent bridge (UCR-2991). Renders no UI — it receives the SDK's
                OptanonWrapper callbacks and gates Adobe Launch, Attentive's tracking and the
                Salesforce DNT signal on the shopper's category choices. Mounted UNGATED, for the
                same reason as the interceptors: if the stub is disabled the handler simply never
                fires, whereas gating it on `oneTrust.enabled` would silently strand any consent
                already recorded on the device. */}
            <OneTrustConsentBridge />
            {/* Opens the OneTrust preference centre when a content-authored "Cookie Settings" CTA
                is clicked (UCR-2991) — the shopper's only route back to their choices. Ungated for
                the same reason as ChatLinkInterceptor above: an authored link must never fall
                through to a dead hash. */}
            <CookieSettingsLinkInterceptor />
        </ComposeProviders>
    );
}

/**
 * Component that executes pending actions after authentication
 * This runs on every route to check if there are actions queued from auth interception
 */
function AuthActionExecutor() {
    useExecutePendingAction();
    return null;
}

/**
 * Revalidates loader data once on back/forward (e.g. back from SFRA). Mounted only when
 * hybrid is enabled; ensures UI is fresh after a full-page redirect. No-op when hybrid is off.
 */
function BackNavigationRevalidator() {
    const revalidator = useRevalidator();
    const didRevalidateRef = useRef(false);
    useEffect(() => {
        if (didRevalidateRef.current) return;
        const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
        if (nav?.type === 'back_forward' && revalidator.state === 'idle') {
            didRevalidateRef.current = true;
            void revalidator.revalidate();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return null;
}

/**
 * Initialize UITarget dev mode overlay (DEV ONLY - zero production overhead)
 * Lazy-loads the overlay when VITE_UI_TARGET_DEV_MODE=true
 */
function UITargetDevModeInit() {
    useEffect(() => {
        // Only runs in browser
        if (typeof window === 'undefined') return;

        // Only in development
        if (import.meta.env.PROD) return;

        // Only if enabled
        if (import.meta.env.VITE_UI_TARGET_DEV_MODE !== 'true') return;

        // Lazy load the overlay
        void import('@/lib/ui-target-dev-mode').then(({ initUITargetDevMode }) => {
            void initUITargetDevMode();
        });
    }, []);

    return null;
}
